import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeaders, mapErrorMessage } from '../../lib/libraryUtils';

/**
 * Hook for library CRUD actions (folders and documents)
 */
export const useLibraryActions = ({ fetchFolders, fetchDocuments, fetchTrash }) => {
  
  // === FOLDER ACTIONS ===
  
  const createFolder = useCallback(async (name, parentId = null) => {
    try {
      await axios.post(`${API}/library/folders`, {
        name,
        parent_folder_id: parentId
      }, getAuthHeaders());
      toast.success(`Map "${name}" aangemaakt`);
      await fetchFolders();
      return true;
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error(mapErrorMessage(error, 'aanmaken map'));
      return false;
    }
  }, [fetchFolders]);

  const renameFolder = useCallback(async (folderId, newName) => {
    try {
      await axios.put(`${API}/library/folders/${folderId}`, {
        name: newName
      }, getAuthHeaders());
      toast.success(`Map hernoemd naar "${newName}"`);
      await fetchFolders();
      return true;
    } catch (error) {
      console.error('Error renaming folder:', error);
      toast.error(mapErrorMessage(error, 'hernoemen map'));
      return false;
    }
  }, [fetchFolders]);

  const deleteFolder = useCallback(async (folderId) => {
    try {
      await axios.delete(`${API}/library/folders/${folderId}`, getAuthHeaders());
      toast.success('Map naar prullenbak verplaatst');
      await Promise.all([fetchFolders(), fetchTrash()]);
      return true;
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error(mapErrorMessage(error, 'verwijderen map'));
      return false;
    }
  }, [fetchFolders, fetchTrash]);

  const moveFolder = useCallback(async (folderId, targetFolderId) => {
    try {
      await axios.put(`${API}/library/folders/${folderId}`, {
        parent_folder_id: targetFolderId || null
      }, getAuthHeaders());
      toast.success('Map verplaatst');
      await fetchFolders();
      return true;
    } catch (error) {
      console.error('Error moving folder:', error);
      toast.error(mapErrorMessage(error, 'verplaatsen map'));
      return false;
    }
  }, [fetchFolders]);

  const restoreFolder = useCallback(async (folderId) => {
    try {
      await axios.post(`${API}/library/folders/${folderId}/restore`, {}, getAuthHeaders());
      toast.success('Map hersteld');
      await Promise.all([fetchFolders(), fetchTrash()]);
      return true;
    } catch (error) {
      console.error('Error restoring folder:', error);
      toast.error(mapErrorMessage(error, 'herstellen map'));
      return false;
    }
  }, [fetchFolders, fetchTrash]);

  // === DOCUMENT ACTIONS ===

  const renameDocument = useCallback(async (docId, newTitle) => {
    try {
      await axios.put(`${API}/library/documents/${docId}`, {
        title: newTitle
      }, getAuthHeaders());
      toast.success(`Document hernoemd naar "${newTitle}"`);
      await fetchDocuments();
      return true;
    } catch (error) {
      console.error('Error renaming document:', error);
      toast.error(mapErrorMessage(error, 'hernoemen document'));
      return false;
    }
  }, [fetchDocuments]);

  const deleteDocument = useCallback(async (docId) => {
    try {
      await axios.delete(`${API}/library/documents/${docId}`, getAuthHeaders());
      toast.success('Document naar prullenbak verplaatst');
      await Promise.all([fetchDocuments(), fetchTrash()]);
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(mapErrorMessage(error, 'verwijderen document'));
      return false;
    }
  }, [fetchDocuments, fetchTrash]);

  const moveDocument = useCallback(async (docId, targetFolderId) => {
    try {
      await axios.put(`${API}/library/documents/${docId}`, {
        folder_id: targetFolderId || null
      }, getAuthHeaders());
      toast.success('Document verplaatst');
      await fetchDocuments();
      return true;
    } catch (error) {
      console.error('Error moving document:', error);
      toast.error(mapErrorMessage(error, 'verplaatsen document'));
      return false;
    }
  }, [fetchDocuments]);

  const restoreDocument = useCallback(async (docId, docTitle) => {
    try {
      await axios.post(`${API}/library/documents/${docId}/restore`, {}, getAuthHeaders());
      toast.success(`Document "${docTitle}" hersteld`);
      await Promise.all([fetchDocuments(), fetchTrash()]);
      return true;
    } catch (error) {
      console.error('Error restoring document:', error);
      toast.error(mapErrorMessage(error, 'herstellen document'));
      return false;
    }
  }, [fetchDocuments, fetchTrash]);

  // === DOWNLOAD/PREVIEW ===

  const downloadDocument = useCallback(async (doc) => {
    try {
      const response = await fetch(
        `${API}/library/documents/${doc.id}/download`,
        { ...getAuthHeaders() }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_name || `${doc.title}.${doc.extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return true;
      } else {
        toast.error('Download mislukt');
        return false;
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(mapErrorMessage(error, 'downloaden'));
      return false;
    }
  }, []);

  const getPreviewUrl = useCallback(async (docId) => {
    try {
      const response = await axios.get(
        `${API}/library/documents/${docId}/preview`,
        getAuthHeaders()
      );
      return response.data.url;
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(mapErrorMessage(error, 'preview laden'));
      return null;
    }
  }, []);

  return {
    // Folder actions
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    restoreFolder,

    // Document actions
    renameDocument,
    deleteDocument,
    moveDocument,
    restoreDocument,

    // Download/Preview
    downloadDocument,
    getPreviewUrl,
  };
};

export default useLibraryActions;
